import { Shield } from 'lucide-react';

// Helper to deeply extract text from nested objects
export const extractText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(item => extractText(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const textFields = ['text', 'description', 'content', 'message', 'value', 'name', 'title', 'activity', 'overview', 'details', 'info'];
    for (const field of textFields) {
      if (value[field] && typeof value[field] === 'string') return value[field];
    }
    const texts = Object.values(value)
      .filter(v => typeof v === 'string')
      .join('. ');
    if (texts) return texts;
    return Object.values(value).map(v => extractText(v)).filter(Boolean).join('. ');
  }
  return String(value);
};

// Render a list of items (handles arrays and objects)
export const renderList = (items, keyPrefix = 'item') => {
  if (!items) return null;
  if (typeof items === 'string') return <p className="text-charcoal">{items}</p>;
  
  if (Array.isArray(items)) {
    return (
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={`${keyPrefix}-${i}`} className="flex gap-2 text-sm text-charcoal">
            <span className="text-forest shrink-0">&bull;</span>
            <span>{extractText(item)}</span>
          </li>
        ))}
      </ul>
    );
  }
  
  if (typeof items === 'object') {
    return (
      <div className="space-y-3">
        {Object.entries(items).map(([key, val]) => (
          <div key={key} className="bg-sand/30 p-3 rounded-lg">
            <p className="font-medium text-forest capitalize mb-1">{key.replace(/_/g, ' ')}</p>
            {Array.isArray(val) ? (
              <ul className="space-y-1 ml-2">
                {val.map((v, i) => (
                  <li key={`${key}-val-${i}`} className="text-sm text-charcoal flex gap-2">
                    <span className="text-burnt">&rarr;</span>
                    {extractText(v)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-charcoal">{extractText(val)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }
  
  return <p className="text-charcoal">{String(items)}</p>;
};

// Render hotel recommendations
export const renderHotels = (hotels) => {
  if (!hotels) return null;
  const hotelList = Array.isArray(hotels) ? hotels : [hotels];
  return (
    <div className="grid gap-4">
      {hotelList.slice(0, 5).map((hotel, i) => (
        <div key={`hotel-${hotel.name || i}`} className="p-4 bg-forest/5 rounded-lg border border-forest/20">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-medium text-charcoal">{extractText(hotel.name || hotel.title || hotel)}</h4>
              {hotel.area && <p className="text-sm text-charcoal/70">{extractText(hotel.area)}</p>}
              {hotel.location && <p className="text-sm text-charcoal/70">{extractText(hotel.location)}</p>}
            </div>
            {(hotel.price || hotel.price_per_night || hotel.price_range) && (
              <span className="bg-burnt/10 text-burnt px-3 py-1 rounded-full text-sm font-medium">
                {extractText(hotel.price_per_night || hotel.price || hotel.price_range)}
              </span>
            )}
          </div>
          {hotel.description && <p className="text-sm text-charcoal/70 mt-2">{extractText(hotel.description)}</p>}
          {hotel.amenities && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(Array.isArray(hotel.amenities) ? hotel.amenities : [hotel.amenities]).map((a, j) => (
                <span key={`amenity-${j}`} className="px-2 py-0.5 bg-forest/10 text-forest text-xs rounded">{extractText(a)}</span>
              ))}
            </div>
          )}
          {hotel.rating && <p className="text-sm text-burnt mt-1">&#9733; {extractText(hotel.rating)}</p>}
        </div>
      ))}
    </div>
  );
};

// Render cultural do's and don'ts
export const renderDosAndDonts = (etiquette) => {
  if (!etiquette) return <p className="text-charcoal/60">No cultural guidance available</p>;
  if (typeof etiquette === 'string') return <p className="text-charcoal">{etiquette}</p>;
  if (Array.isArray(etiquette)) return renderList(etiquette);

  const sections = [];
  const dosKeys = ['dos', 'do', 'should', 'recommended', 'tips'];
  for (const key of dosKeys) {
    if (etiquette[key]) {
      sections.push(
        <div key="dos" className="mb-4">
          <p className="text-xs uppercase tracking-wide text-forest font-medium mb-2">&#10003; Do&apos;s</p>
          <ul className="space-y-1">
            {(Array.isArray(etiquette[key]) ? etiquette[key] : [etiquette[key]]).map((item, i) => (
              <li key={`do-${i}`} className="text-sm text-charcoal flex gap-2">
                <span className="text-forest">&#10003;</span>
                {extractText(item)}
              </li>
            ))}
          </ul>
        </div>
      );
      break;
    }
  }
  const dontsKeys = ['donts', 'dont', 'avoid', 'should_not', 'warnings'];
  for (const key of dontsKeys) {
    if (etiquette[key]) {
      sections.push(
        <div key="donts" className="mb-4">
          <p className="text-xs uppercase tracking-wide text-red-600 font-medium mb-2">&#10007; Don&apos;ts</p>
          <ul className="space-y-1">
            {(Array.isArray(etiquette[key]) ? etiquette[key] : [etiquette[key]]).map((item, i) => (
              <li key={`dont-${i}`} className="text-sm text-charcoal flex gap-2">
                {extractText(item)}
              </li>
            ))}
          </ul>
        </div>
      );
      break;
    }
  }
  const culturalKeys = ['clothing', 'attire', 'dress_code', 'manners', 'customs', 'local_customs', 'etiquette', 'greetings', 'dining', 'religious'];
  for (const key of culturalKeys) {
    if (etiquette[key]) {
      sections.push(
        <div key={key} className="mb-4 p-3 bg-sand/50 rounded-lg">
          <p className="text-xs uppercase tracking-wide text-burnt font-medium mb-2">
            {key.replace(/_/g, ' ')}
          </p>
          {Array.isArray(etiquette[key]) ? (
            <ul className="space-y-1">
              {etiquette[key].map((item, i) => (
                <li key={`${key}-${i}`} className="text-sm text-charcoal flex gap-2">
                  {extractText(item)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-charcoal">{extractText(etiquette[key])}</p>
          )}
        </div>
      );
    }
  }
  if (sections.length === 0) {
    return (
      <div className="space-y-3">
        {Object.entries(etiquette).map(([key, val]) => (
          <div key={key} className="p-3 bg-sand/30 rounded-lg">
            <p className="font-medium text-forest capitalize mb-1">{key.replace(/_/g, ' ')}</p>
            {Array.isArray(val) ? (
              <ul className="space-y-1">
                {val.map((v, i) => (
                  <li key={`${key}-val-${i}`} className="text-sm text-charcoal flex gap-2">
                    <span className="text-burnt">&rarr;</span>
                    {extractText(v)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-charcoal">{extractText(val)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }
  return <div>{sections}</div>;
};

// Render visa information
export const renderVisaInfo = (visaInfo) => {
  if (!visaInfo) return <p className="text-charcoal/60">No visa information available</p>;
  if (typeof visaInfo === 'string') return <p className="text-charcoal">{visaInfo}</p>;
  return (
    <div className="space-y-3">
      {visaInfo.requirements && (
        <div className="p-3 bg-forest/5 rounded-lg">
          <p className="font-medium text-forest mb-2">Requirements</p>
          <p className="text-sm text-charcoal">{extractText(visaInfo.requirements)}</p>
        </div>
      )}
      {visaInfo.processing_time && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal/60">Processing Time:</span>
          <span className="text-charcoal">{extractText(visaInfo.processing_time)}</span>
        </div>
      )}
      {visaInfo.validity && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal/60">Validity:</span>
          <span className="text-charcoal">{extractText(visaInfo.validity)}</span>
        </div>
      )}
      {visaInfo.cost && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal/60">Cost:</span>
          <span className="text-burnt font-medium">{extractText(visaInfo.cost)}</span>
        </div>
      )}
      {visaInfo.website && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal/60">Official Website:</span>
          <span className="text-forest">{extractText(visaInfo.website)}</span>
        </div>
      )}
      {visaInfo.notes && (
        <p className="text-sm text-charcoal/70 italic">{extractText(visaInfo.notes)}</p>
      )}
      {Object.entries(visaInfo)
        .filter(([k]) => !['requirements', 'processing_time', 'validity', 'cost', 'website', 'notes'].includes(k))
        .map(([key, val]) => (
          <div key={key} className="text-sm">
            <span className="text-charcoal/60 capitalize">{key.replace(/_/g, ' ')}: </span>
            <span className="text-charcoal">{extractText(val)}</span>
          </div>
        ))
      }
    </div>
  );
};

// Render weather information
export const renderWeatherInfo = (weatherInfo) => {
  if (!weatherInfo) return <p className="text-charcoal/60">No weather information available</p>;
  if (typeof weatherInfo === 'string') return <p className="text-charcoal">{weatherInfo}</p>;
  return (
    <div className="space-y-3">
      {weatherInfo.average_temperature && (
        <div className="p-3 bg-burnt/5 rounded-lg flex items-center gap-3">
          <div>
            <p className="font-medium text-charcoal">Temperature</p>
            <p className="text-sm text-charcoal/70">{extractText(weatherInfo.average_temperature)}</p>
          </div>
        </div>
      )}
      {weatherInfo.precipitation && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal">{extractText(weatherInfo.precipitation)}</span>
        </div>
      )}
      {weatherInfo.humidity && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-charcoal/60">Humidity:</span>
          <span className="text-charcoal">{extractText(weatherInfo.humidity)}</span>
        </div>
      )}
      {weatherInfo.clothing_recommendations && (
        <div className="p-3 bg-sand/50 rounded-lg">
          <p className="font-medium text-forest mb-1">What to Pack</p>
          <p className="text-sm text-charcoal">{extractText(weatherInfo.clothing_recommendations)}</p>
        </div>
      )}
      {weatherInfo.considerations && (
        <p className="text-sm text-charcoal/70 italic">{extractText(weatherInfo.considerations)}</p>
      )}
      {weatherInfo.best_time && (
        <div className="text-sm">
          <span className="text-charcoal/60">Best Time to Visit: </span>
          <span className="text-forest font-medium">{extractText(weatherInfo.best_time)}</span>
        </div>
      )}
      {Object.entries(weatherInfo)
        .filter(([k]) => !['average_temperature', 'precipitation', 'humidity', 'clothing_recommendations', 'considerations', 'best_time'].includes(k))
        .map(([key, val]) => (
          <div key={key} className="text-sm">
            <span className="text-charcoal/60 capitalize">{key.replace(/_/g, ' ')}: </span>
            <span className="text-charcoal">{extractText(val)}</span>
          </div>
        ))
      }
    </div>
  );
};

// Render safety tips
export const renderSafetyTips = (safetyTips) => {
  if (!safetyTips) return <p className="text-charcoal/60">No safety tips available</p>;
  if (typeof safetyTips === 'string') return <p className="text-charcoal">{safetyTips}</p>;
  if (Array.isArray(safetyTips)) {
    return (
      <ul className="space-y-2">
        {safetyTips.map((tip, i) => (
          <li key={`safety-${i}`} className="flex gap-2 text-sm text-charcoal">
            <Shield className="w-4 h-4 text-forest shrink-0 mt-0.5" />
            <span>{extractText(tip)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="space-y-4">
      {Object.entries(safetyTips).map(([category, tips]) => (
        <div key={category}>
          <p className="text-xs uppercase tracking-wide text-forest font-medium mb-2">
            {category.replace(/_/g, ' ')}
          </p>
          {Array.isArray(tips) ? (
            <ul className="space-y-1 ml-1">
              {tips.map((tip, i) => (
                <li key={`${category}-tip-${i}`} className="flex gap-2 text-sm text-charcoal">
                  <span className="text-forest">&bull;</span>
                  {extractText(tip)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-charcoal">{extractText(tips)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to clean array strings
export const cleanArrayString = (value) => {
  if (!value) return '';
  let str = String(value);
  str = str.replace(/^\["|^\["|\["$|"\]$/g, '');
  str = str.replace(/^\{"|^\{"|\{"$|"\}$/g, '');
  str = str.replace(/^["']|["']$/g, '');
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return parsed.join('\n\u2022 ');
      }
    } catch (e) {
      str = str.slice(1, -1).replace(/","/g, '\n\u2022 ').replace(/"/g, '');
    }
  }
  return str;
};

export const getString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return cleanArrayString(value);
  }
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(item => cleanArrayString(item)).filter(Boolean).join('\n\u2022 ');
  }
  if (typeof value === 'object') {
    return value.text || value.name || value.title || value.description || value.activity || '';
  }
  return String(value);
};

// Safe render function for any value type
export const safeRender = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((item, i) => (
      <span key={`sr-${i}`}>
        {safeRender(item)}
        {i < value.length - 1 && ', '}
      </span>
    ));
  }
  if (typeof value === 'object') {
    const textFields = ['text', 'description', 'content', 'name', 'title', 'value', 'overview'];
    for (const field of textFields) {
      if (value[field] && typeof value[field] === 'string') return value[field];
    }
    const stringValues = Object.values(value).filter(v => typeof v === 'string');
    if (stringValues.length > 0) return stringValues.join('. ');
    return JSON.stringify(value);
  }
  return String(value);
};

// Render estimated cost - handles various formats
export const renderEstimatedCost = (cost) => {
  if (!cost) return 'N/A';
  if (typeof cost === 'string') return cost;
  if (typeof cost === 'number') return `$${cost}`;
  if (typeof cost === 'object') {
    if (cost.min !== undefined && cost.max !== undefined) return `$${cost.min} - $${cost.max}`;
    if (cost.low !== undefined && cost.high !== undefined) return `$${cost.low} - $${cost.high}`;
    if (cost.amount !== undefined) return cost.currency ? `${cost.currency}${cost.amount}` : `$${cost.amount}`;
    if (cost.value !== undefined) return typeof cost.value === 'number' ? `$${cost.value}` : cost.value;
    if (cost.total !== undefined) return typeof cost.total === 'number' ? `$${cost.total}` : cost.total;
    const values = Object.values(cost);
    for (const v of values) {
      if (typeof v === 'string' && v.includes('$')) return v;
      if (typeof v === 'number') return `$${v}`;
      if (typeof v === 'string') return v;
    }
    return JSON.stringify(cost);
  }
  return String(cost);
};

// Render recommendations - handles arrays, objects, and strings
export const renderRecommendations = (recommendations) => {
  if (!recommendations) return null;
  if (typeof recommendations === 'string') return <p className="text-charcoal">{recommendations}</p>;
  if (Array.isArray(recommendations)) {
    return (
      <ul className="space-y-1">
        {recommendations.map((item, i) => (
          <li key={`rec-${i}`} className="text-sm text-charcoal flex gap-2">
            <span className="text-burnt">&bull;</span>
            <span>{typeof item === 'object' ? safeRender(item) : item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (typeof recommendations === 'object') {
    return (
      <div className="space-y-2">
        {Object.entries(recommendations).map(([key, val]) => (
          <div key={key} className="text-sm">
            <span className="font-medium text-forest capitalize">{key.replace(/_/g, ' ')}: </span>
            <span className="text-charcoal">{safeRender(val)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <p className="text-charcoal">{String(recommendations)}</p>;
};

export const createGoogleMapsLink = (location) => {
  if (!location) return '#';
  const query = encodeURIComponent(location);
  return `https://maps.google.com/maps?q=${query}`;
};

